import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Quotations from "@/pages/Quotations";
import QuotationPrint from "@/pages/QuotationPrint";
import Orders from "@/pages/Orders";
import Trucks from "@/pages/Trucks";
import Machines from "@/pages/Machines";
import RequestsPage from "@/pages/Requests";
import Notifications from "@/pages/Notifications";
import UsersPage from "@/pages/Users";
import ActivityLog from "@/pages/ActivityLog";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Journals from "@/pages/Journals";
import TrialBalance from "@/pages/TrialBalance";
import AccountsPayable from "@/pages/AccountsPayable";
import BankReconciliation from "@/pages/BankReconciliation";
import CostIntelligence from "@/pages/CostIntelligence";
import FixedAssets from "@/pages/FixedAssets";
import AccountsReceivable from "@/pages/AccountsReceivable";
import AutomationRules from "@/pages/AutomationRules";
import Production from "@/pages/Production";
import DeliveryNotePrint from "@/pages/DeliveryNotePrint";
import WeeklyBudget from "@/pages/WeeklyBudget";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Invoices from "@/pages/Invoices";
import InvoicePrint from "@/pages/InvoicePrint";
import StockTake from "@/pages/StockTake";
import Projects from "@/pages/Projects";
import FuelLogs from "@/pages/FuelLogs";
import HR from "@/pages/HR";
import Settings from "@/pages/Settings";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
import RequestDetail from "@/pages/RequestDetail";
import FuelLogDetail from "@/pages/FuelLogDetail";
import MachineDetail from "@/pages/MachineDetail";
import EmployeeDetail from "@/pages/EmployeeDetail";
import DeliveryNoteDetail from "@/pages/DeliveryNoteDetail";
import UserProfile from "@/pages/UserProfile";
import MaintenanceRecord, { MaintenanceRecordsList } from "@/pages/MaintenanceRecord";
import Payroll from "@/pages/Payroll";
import StockIssuances from "@/pages/StockIssuances";
import Rosters from "@/pages/Rosters";
import DailyHours from "@/pages/DailyHours";
import Organizations from "@/pages/Organizations";
import Leads from "@/pages/Leads";
import ChangePasswordGate from "@/components/ChangePasswordGate";
import OnboardingWizard from "@/components/OnboardingWizard";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="phizo-label">Loading control room…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// LUROMA provider can only reach the org-management surfaces. Any other
// route bounces them to /organizations. Prevents accidental cross-tenant
// data views (provider is otherwise unfiltered).
function ProviderGate({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const PROVIDER_ALLOWED = ["/organizations", "/leads", "/notifications", "/login"];
  if (user && user.role === "luroma_provider") {
    const allowed = PROVIDER_ALLOWED.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/organizations" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={<Protected><ChangePasswordGate><OnboardingWizard><ProviderGate><Layout /></ProviderGate></OnboardingWizard></ChangePasswordGate></Protected>}
            >
              <Route index element={<Dashboard />} />
              <Route path="production" element={<Production />} />
              <Route path="weekly-budget" element={<WeeklyBudget />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="stock-take" element={<StockTake />} />
              <Route path="purchase-orders" element={<PurchaseOrders />} />
              <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="orders" element={<Orders />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="customers" element={<Customers />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="projects" element={<Projects />} />
              <Route path="fuel-logs" element={<FuelLogs />} />
              <Route path="fuel-logs/:id" element={<FuelLogDetail />} />
              <Route path="hr" element={<HR />} />
              <Route path="hr/:id" element={<EmployeeDetail />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="rosters" element={<Rosters />} />
              <Route path="daily-hours" element={<DailyHours />} />
              <Route path="stock-issuances" element={<StockIssuances />} />
              <Route path="trucks" element={<Trucks />} />
              <Route path="machines" element={<Machines />} />
              <Route path="machines/:id" element={<MachineDetail />} />
              <Route path="maintenance-records" element={<MaintenanceRecordsList />} />
              <Route path="maintenance-records/new" element={<MaintenanceRecord />} />
              <Route path="maintenance-records/:id" element={<MaintenanceRecord />} />
              <Route path="requests" element={<RequestsPage />} />
              <Route path="requests/:id" element={<RequestDetail />} />
              <Route path="delivery-notes/:id" element={<DeliveryNoteDetail />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="activity" element={<ActivityLog />} />
              <Route path="finance/coa" element={<ChartOfAccounts />} />
              <Route path="finance/journals" element={<Journals />} />
              <Route path="finance/trial-balance" element={<TrialBalance />} />
              <Route path="finance/ap" element={<AccountsPayable />} />
              <Route path="finance/bank-rec" element={<BankReconciliation />} />
              <Route path="finance/ar" element={<AccountsReceivable />} />
              <Route path="finance/fixed-assets" element={<FixedAssets />} />
              <Route path="finance/cost-intel" element={<CostIntelligence />} />
              <Route path="automation" element={<AutomationRules />} />
              <Route path="users/:id" element={<UserProfile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="leads" element={<Leads />} />
            </Route>
            <Route
              path="/quotations/:id/print"
              element={<Protected><QuotationPrint /></Protected>}
            />
            <Route
              path="/delivery-notes/:id/print"
              element={<Protected><DeliveryNotePrint /></Protected>}
            />
            <Route
              path="/invoices/:id/print"
              element={<Protected><InvoicePrint /></Protected>}
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
